<template>
    <fwb-modal :persistent="noClose" v-if="isShowModal" size="3xl" @close="closeModal">
        <template #header>
            <div class="flex w-full items-start justify-between gap-4">
                <div>
                    <p class="text-[11px] font-bold uppercase tracking-[0.16em] text-orange-500">Descargas</p>
                    <h3 class="mt-1 text-xl font-black text-slate-900">{{ title }}</h3>
                    <p class="mt-1 text-sm text-slate-500">Completa los datos y publica el archivo para el portal.</p>
                </div>
            </div>
        </template>

        <template #body>
            <div class="space-y-5">
                <div>
                    <label for="nombre_descarga" class="mb-2 block text-sm font-semibold text-slate-700">
                        Nombre de la descarga <span class="text-orange-500">*</span>
                    </label>
                    <input
                        id="nombre_descarga"
                        name="nombre_descarga"
                        type="text"
                        required
                        v-model.text="download.name"
                        placeholder="Ej. Firmware Hikvision 2026"
                        class="ts-modal-input border-slate-200"
                        :class="{ '!border-red-400 !ring-4 !ring-red-200': form.name }"
                    >
                    <small class="mt-1 block text-xs text-red-500" v-if="form.name">Campo requerido</small>
                </div>

                <div class="grid gap-4 sm:grid-cols-2">
                    <div class="relative z-10">
                        <label class="mb-2 block text-sm font-semibold text-slate-700">
                            Marca <span class="text-orange-500">*</span>
                        </label>
                        <BrandSearchSelect
                            v-model="download.brands"
                            :options="availableBrands"
                            :error="!!form.brand"
                            placeholder="Buscar y seleccionar marca..."
                        />
                    </div>

                    <div class="flex items-end">
                        <label
                            class="flex w-full cursor-pointer items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 transition hover:border-orange-300"
                        >
                            <div>
                                <p class="text-sm font-semibold text-slate-700">Estado</p>
                                <p class="text-xs text-slate-500">Visible en el portal</p>
                            </div>
                            <fwb-checkbox v-model="download.active" label="Activa" />
                        </label>
                    </div>
                </div>

                <div>
                    <label for="message" class="mb-2 block text-sm font-semibold text-slate-700">
                        Descripción <span class="text-orange-500">*</span>
                    </label>
                    <textarea
                        name="message"
                        id="message"
                        rows="4"
                        v-model="download.description"
                        placeholder="Describe el contenido del archivo..."
                        class="ts-modal-input min-h-[110px] resize-y border-slate-200"
                    ></textarea>
                </div>

                <div>
                    <label class="mb-2 block text-sm font-semibold text-slate-700">
                        Archivo <span class="text-orange-500">*</span>
                    </label>

                    <div
                        v-if="!uploadingFile"
                        class="relative overflow-hidden rounded-2xl border-2 border-dashed transition"
                        :class="isDragging
                            ? 'border-orange-500 bg-orange-50'
                            : (download.filename
                                ? 'border-emerald-300 bg-emerald-50/40'
                                : 'border-slate-300 bg-slate-50 hover:border-orange-400 hover:bg-orange-50/40')"
                        @dragenter.prevent="isDragging = true"
                        @dragover.prevent="isDragging = true"
                        @dragleave.prevent="isDragging = false"
                        @drop.prevent="onDrop"
                    >
                        <input
                            type="file"
                            id="fileupload"
                            name="fileupload"
                            accept=".rar,.zip"
                            @change="uploadFile"
                            ref="fileupload"
                            class="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
                        >

                        <div class="relative z-0 flex flex-col items-center justify-center px-6 py-8 text-center">
                            <div
                                class="mb-3 inline-flex h-14 w-14 items-center justify-center rounded-2xl shadow-sm"
                                :class="download.filename
                                    ? 'bg-emerald-500 text-white'
                                    : 'bg-gradient-to-br from-orange-500 to-orange-600 text-white'"
                            >
                                <CheckCircleIcon v-if="download.filename" class="h-7 w-7" />
                                <CloudUploadIcon v-else class="h-7 w-7" />
                            </div>

                            <template v-if="download.filename">
                                <p class="text-sm font-bold text-emerald-700">Archivo listo</p>
                                <p class="mt-1 max-w-md truncate text-sm font-medium text-slate-700">{{ download.filename }}</p>
                                <p class="mt-2 text-xs text-slate-500">Haz clic o suelta otro archivo para reemplazarlo</p>
                            </template>
                            <template v-else>
                                <p class="text-sm font-bold text-slate-800">
                                    Arrastra tu archivo aquí o
                                    <span class="text-orange-600">haz clic para seleccionar</span>
                                </p>
                                <p class="mt-2 text-xs text-slate-500">Formatos permitidos: ZIP o RAR</p>
                            </template>
                        </div>
                    </div>

                    <div v-if="uploadingFile" class="rounded-2xl border border-orange-200 bg-orange-50/60 p-5">
                        <div class="mb-3 flex items-center gap-3">
                            <span class="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-orange-500 text-white">
                                <CloudUploadIcon class="h-5 w-5" />
                            </span>
                            <div>
                                <p class="text-sm font-bold text-slate-800">Subiendo archivo...</p>
                                <p class="text-xs text-slate-500">No cierres esta ventana hasta finalizar</p>
                            </div>
                        </div>
                        <fwb-progress :progress="progress" label-position="inside" label-progress size="lg" />
                    </div>

                    <p v-if="download.id != 0 && download.path" class="mt-3 text-sm">
                        <a
                            class="inline-flex items-center gap-1.5 font-semibold text-orange-600 underline hover:text-orange-500"
                            target="_blank"
                            :href="route('file.get', [download.id])"
                        >
                            Descargar archivo actual:
                            {{ download.path.substring(download.path.indexOf('/') + 1) }}
                        </a>
                    </p>
                </div>

                <div class="flex flex-col-reverse gap-3 border-t border-slate-100 pt-4 sm:flex-row sm:justify-end">
                    <button
                        type="button"
                        class="inline-flex items-center justify-center rounded-2xl border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
                        :disabled="noClose"
                        @click="closeModal"
                    >
                        Cancelar
                    </button>

                    <button
                        v-if="!savingDownload"
                        type="button"
                        class="ts-btn-primary inline-flex items-center justify-center gap-2 disabled:opacity-50"
                        :disabled="validateData"
                        @click="saveDownload"
                    >
                        <SaveIcon class="h-4 w-4" />
                        Guardar
                    </button>

                    <span
                        v-else
                        class="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-700 px-5 py-3 text-sm font-semibold text-white"
                    >
                        <svg class="h-5 w-5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Guardando...
                    </span>
                </div>
            </div>
        </template>
    </fwb-modal>
</template>

<script>
import { FwbModal, FwbCheckbox, FwbProgress } from 'flowbite-vue';
import { SaveIcon, CloudUploadIcon, CheckCircleIcon } from '@heroicons/vue/outline';
import BrandSearchSelect from './BrandSearchSelect.vue';
import Swal from 'sweetalert2';

export default {
    components: {
        FwbModal,
        SaveIcon,
        CloudUploadIcon,
        CheckCircleIcon,
        FwbCheckbox,
        FwbProgress,
        BrandSearchSelect,
    },
    data() {
        return {
            progress: 0,
            isShowModal: false,
            uploadingFile: false,
            savingDownload: false,
            isDragging: false,
            form: {},
            download: {
                id: 0,
                name: '',
                tempfile: '',
                filename: '',
                active: true,
                lines: [0],
                categories: [0],
                subcategories: [0],
                brands: '',
                description: '',
            },
            noClose: false,
            alertMessage: {
                alert: 0,
                message: '',
            },
        };
    },
    props: {
        title: String,
        downloads: Array | Object,
        brands: Array | Object,
        user_access: Array | Object,
    },
    watch: {
        downloads: function (newval) {
            this.download = newval;
        },
    },
    computed: {
        availableBrands() {
            const list = Array.isArray(this.brands)
                ? this.brands
                : (this.brands ? Object.values(this.brands) : []);

            return list.filter((brand) =>
                this.user_access?.access_level_id == 3 || brand.user_has_access
            );
        },
        validateData() {
            if (this.download.id != 0) {
                return !(this.download.brands != '' && this.download.name != '' && this.download.path != '' && this.download.description != '' && this.download.description != null);
            }

            return !(this.download.brands != '' && this.download.name != '' && this.download.tempfile != '' &&
                this.download.filename != '' && this.download.description != '' && this.download.description != null);
        },
    },
    emits: ['updateTable'],
    methods: {
        closeModal() {
            if (this.download.tempfile != '') {
                this.deleteFile();
            }
            this.clearModal();
            this.isShowModal = false;
        },
        deleteFile() {
            axios.post(route('file.delete'), {
                file: this.download.tempfile,
            });
        },
        clearModal() {
            this.download = {
                id: 0,
                name: '',
                tempfile: '',
                filename: '',
                active: true,
                lines: [0],
                categories: [0],
                subcategories: [0],
                brands: '',
                description: '',
            };
            this.uploadingFile = false;
            this.savingDownload = false;
            this.progress = 0;
            this.isDragging = false;
        },
        showModal() {
            this.isShowModal = true;
        },
        onDrop(event) {
            this.isDragging = false;
            const files = event.dataTransfer?.files;
            if (!files || !files.length) return;

            const input = this.$refs.fileupload;
            if (!input) return;

            const dataTransfer = new DataTransfer();
            dataTransfer.items.add(files[0]);
            input.files = dataTransfer.files;
            this.uploadFile({ target: input });
        },
        uploadFile(e) {
            let file = this.$refs.fileupload.files[0];
            if (!file) return;

            const formData = new FormData();
            formData.append('fileupload', file);
            formData.append('previousFile', this.download.tempfile);

            const headers = { 'Content-Type': 'multipart/form-data', 'X-CSRF-TOKEN': this.$page.props.csrf_token };
            this.noClose = true;
            this.uploadingFile = true;
            this.isDragging = false;

            axios.post(route('fileup'), formData, {
                headers,
                onUploadProgress: (progressEvent) => {
                    this.progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                },
            }).then((response) => {
                this.uploadingFile = false;
                this.noClose = false;
                if (response.data.status == true) {
                    this.download.tempfile = response.data.file;
                    this.download.filename = response.data.name;
                } else {
                    e.target.value = '';
                    this.messages('warning', response.data.message);
                }
            }).catch(() => {
                this.noClose = false;
                this.uploadingFile = false;
                e.target.value = '';
                this.messages('error', 'Ha ocurrido un error');
            });
        },
        saveDownload() {
            this.noClose = true;
            this.savingDownload = true;
            axios.post(route('save.download'), {
                data: JSON.stringify(this.download),
            }).then((response) => {
                let data = response.data;
                this.noClose = false;
                this.savingDownload = false;
                if (data.status == true) {
                    this.messages('success', 'Descarga guardada exitosamente');
                    this.success();
                } else {
                    this.alertMessage = { alert: 2, message: 'Ocurrió un error al guardar la Descarga' };
                    this.messages('error', 'Ha ocurrido un error');
                }
            }).catch(() => {
                this.noClose = false;
                this.alertMessage = { alert: 2, message: 'Ocurrió un error en el proceso de guardar la Descarga' };
                this.log('error', 'saveDownload', this.alertMessage.message);
                this.savingDownload = false;
            });
        },
        log(type, place, message) {
            switch (type) {
                case 'info':
                    console.info(`${place}: Text. ${message}`);
                    break;
                case 'error':
                    console.error(`Error :  ${place}: Text. ${message}`);
                    break;
            }
        },
        success() {
            this.$emit('updateTable');
            this.closeModal();
        },
        messages(icono, text) {
            Swal.fire({
                position: 'center',
                icon: icono,
                title: text,
                showConfirmButton: false,
                timer: 2500,
            });
        },
    },
};
</script>
